import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useApiCaller } from '../utils/ApiUtilities';
import { getAdminQuestionPutUrl, getTagUrl, getAdminQuestionGroupByIdUrl } from '../utils/UrlUtilities';
import { constructQuestionPostPayload } from '../utils/PayloadUtils';
import {
    BCS_LEVEL,
    QUESTIONDIFFICULTY,
    QUESTION_TYPES,
    QUESTION_TYPE_LABELS,
    TC_BLANK_LABELS,
    SIMILARITY_THRESHOLDS,
    SYLLABUS_STATUS_OPTIONS,
    TAGLEVELS,
    BOARD_VALUES,
    DIVISION_VALUES,
    UNIT_VALUES,
    isBoardLevel,
    isDivisionLevel,
    isUnitLevel,
    isLanguageLevel,
    isTimerLevel,
    isEssaySubject,
    sortBcsTerms,
} from '../utils/Constants';
import useQuestionAi from '../utils/useQuestionAi';
import useVoiceDictation from '../utils/useVoiceDictation';
import { normalizeFieldText, beautifyText } from '../utils/BasicUtilities';
import { formatBdPst } from '../utils/DateUtilities';
import VoiceInputButton from './VoiceInputButton';
import MediaUploadField from './MediaUploadField';
import MathText from './ui/MathText';
import SimilarQuestionsPanel from './ingestion/SimilarQuestionsPanel';
import SourceDocViewer from './SourceDocViewer';
import CloseButton from './ui/CloseButton';
import QuestionPreviewModal from './QuestionPreviewModal';
import YearPicker from './ui/YearPicker';
import './components.css';

const formatDateTime = (iso) => {
    if (!iso) return null;
    return formatBdPst(iso);
};

// Compact author/review strip rendered above the edit form. Reads from the
// QuestionAdminOut row the grid already passed in — no extra API call.
const AuditHeader = ({ question }) => {
    const showRevisionBanner = question?.needs_revision && !question?.revision_resolved;
    const showReviewed = question?.super_admin_reviewed && !showRevisionBanner;
    return (
        <div className="mb-3 small">
            <div className="text-muted">
                {question?.created_by
                    ? <>Created by <strong>{question.created_by.name}</strong></>
                    : <>Created</>}
                {question?.created_at && <> on {formatDateTime(question.created_at)}</>}
                {(question?.updated_by || question?.updated_at) && (
                    <>
                        {' '}·{' '}
                        {question?.updated_by
                            ? <>Last updated by <strong>{question.updated_by.name}</strong></>
                            : <>Last updated</>}
                        {question?.updated_at && <> on {formatDateTime(question.updated_at)}</>}
                    </>
                )}
            </div>
            {showRevisionBanner && (
                <div className="alert alert-warning py-2 mt-2 mb-0" role="alert">
                    <strong>Needs revision.</strong>{' '}
                    {question.super_admin_review_note
                        ? question.super_admin_review_note
                        : 'A super-admin flagged this question for revision.'}
                </div>
            )}
            {showReviewed && (
                <div className="text-success mt-1">
                    ✓ Reviewed
                    {question.super_admin_reviewed_by
                        ? <> by <strong>{question.super_admin_reviewed_by.name}</strong></>
                        : null}
                    {question.super_admin_reviewed_at && <> on {formatDateTime(question.super_admin_reviewed_at)}</>}
                </div>
            )}
        </div>
    );
};

const splitCsv = (v) => (v ? String(v).split(',').map(s => s.trim()).filter(Boolean) : []);

// Canonical serialization of the editable fields — used to detect unsaved changes before
// navigating (Next/Prev). Both the "clean" snapshot (from the question) and the current form
// state are run through this so an unedited form compares equal.
const editableSig = (v) => JSON.stringify({
    qt: v.questionText || '',
    opts: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].map(l => (v.options && v.options[l]) || ''),
    correct: [...(v.correctOptions || [])].map(s => String(s).toUpperCase()).sort(),
    order: v.correctOrder || [],
    type: v.questionType || 'MCQ',
    textAns: v.textAnswer || '',
    expl: v.answerExplanation || '',
    levels: v.levels || [],
    diff: v.difficulty || '',
    years: v.selectedYears || [],
    syl: v.syllabusStatus || 'CURRENT',
    subs: v.selectedSubjects || [],
    tops: v.selectedTopics || [],
    exams: v.selectedExams || [],
    bcs: v.selectedBcsTerm || '',
    board: v.board || '', div: v.division || '', unit: v.unit || '',
    side: v.sideText || '', audio: v.audioUrl || '', solve: v.solvingTimeSeconds || '',
    qImg: v.questionImageUrl || '', optImgs: v.optionImages || {}, explImg: v.explanationImageUrl || '',
    blanks: v.blanks || [], accepted: v.acceptedAnswers || '', matchR: v.matchRows || [], matchImg: v.matchImage || '',
});

// Mirror of the seeding effect below — builds the editable shape from a question row so the
// "clean" signature matches the form's initial state exactly.
const questionToEditable = (q) => ({
    questionText: q.question_text || '',
    options: { A: q.option_a || '', B: q.option_b || '', C: q.option_c || '', D: q.option_d || '', E: q.option_e || '', F: q.option_f || '', G: q.option_g || '', H: q.option_h || '' },
    correctOptions: splitCsv(q.correct_option),
    correctOrder: Array.isArray(q.metadata?.correct_order) ? q.metadata.correct_order : [],
    questionType: q.question_type || 'MCQ',
    textAnswer: q.text_answer || '',
    answerExplanation: q.answer_explanation || '',
    levels: Array.isArray(q.levels) && q.levels.length ? q.levels : (q.level ? [q.level] : []),
    difficulty: q.difficulty || '',
    selectedYears: q.year ? String(q.year).split(',').filter(Boolean) : [],
    syllabusStatus: q.syllabus_status || 'CURRENT',
    selectedSubjects: q.subject ? q.subject.split(',').filter(Boolean) : [],
    selectedTopics: q.topic ? q.topic.split(',').filter(Boolean) : [],
    selectedExams: q.exam ? q.exam.split(',').filter(Boolean) : [],
    selectedBcsTerm: q.bcs_term || '',
    board: q.board || '', division: q.division || '', unit: q.unit || '',
    sideText: q.side_text || '', audioUrl: q.audio_url || '',
    solvingTimeSeconds: q.solving_time_seconds != null ? String(q.solving_time_seconds) : '',
    questionImageUrl: q.question_image_url || '',
    optionImages: { A: q.option_a_image_url || '', B: q.option_b_image_url || '', C: q.option_c_image_url || '', D: q.option_d_image_url || '', E: q.option_e_image_url || '', F: q.option_f_image_url || '', G: q.option_g_image_url || '', H: q.option_h_image_url || '' },
    explanationImageUrl: q.explanation_image_url || '',
    blanks: (q.metadata?.blanks && q.metadata.blanks.length)
        ? q.metadata.blanks.map(b => ({ choices: [...(b.choices || ['', '', '']), '', '', ''].slice(0, 3), correct: b.correct ?? 0 }))
        : [{ choices: ['', '', ''], correct: 0 }, { choices: ['', '', ''], correct: 0 }],
    acceptedAnswers: Array.isArray(q.metadata?.accepted) ? q.metadata.accepted.join('\n') : '',
    matchRows: (q.metadata?.match_options && Object.keys(q.metadata.match_options).length)
        ? Object.keys(q.metadata.match_options).map(l => ({ letter: l, text: q.metadata.match_options[l] }))
        : [{ letter: 'A', text: '' }, { letter: 'B', text: '' }, { letter: 'C', text: '' }],
    matchImage: q.metadata?.match_image || '',
});

const QuestionEditForm = ({ question, onCancel, onSave, onSaved = null, title = null, onBack = null, onNext = null, backLabel = '◀ Back', nextLabel = 'Next ▶', hasPrev = null, hasNext = null, serialLabel = '', stayOpenOnSave = false, sourceDoc = null, setSourceDoc = null }) => {
    const [questionText, setQuestionText] = useState(question?.question_text || '');
    const [optionA, setOptionA] = useState(question?.option_a || '');
    const [optionB, setOptionB] = useState(question?.option_b || '');
    const [optionC, setOptionC] = useState(question?.option_c || '');
    const [optionD, setOptionD] = useState(question?.option_d || '');
    const [optionE, setOptionE] = useState(question?.option_e || '');
    const [optionF, setOptionF] = useState(question?.option_f || '');
    const [optionG, setOptionG] = useState(question?.option_g || '');
    const [optionH, setOptionH] = useState(question?.option_h || '');
    // Reveal extras that already have content (E–F → 2, any of G–H → 4).
    const [extraOptionsShown, setExtraOptionsShown] = useState(
        (question?.option_g || question?.option_h) ? 4 : (question?.option_e || question?.option_f) ? 2 : 0
    );
    const [blanks, setBlanks] = useState(
        (question?.metadata?.blanks && question.metadata.blanks.length)
            ? question.metadata.blanks.map(b => ({ choices: [...(b.choices || ['', '', '']), '', '', ''].slice(0, 3), correct: b.correct ?? 0 }))
            : [{ choices: ['', '', ''], correct: 0 }, { choices: ['', '', ''], correct: 0 }]
    );
    const [correctOptions, setCorrectOptions] = useState(splitCsv(question?.correct_option));
    // Build-a-Sentence: correct tile order as 0-based indices into the present option letters.
    const [correctOrder, setCorrectOrder] = useState(
        Array.isArray(question?.metadata?.correct_order) ? question.metadata.correct_order : []
    );
    const [questionType, setQuestionType] = useState(question?.question_type || 'MCQ');
    const [textAnswer, setTextAnswer] = useState(question?.text_answer || '');
    const [answerExplanation, setAnswerExplanation] = useState(question?.answer_explanation || '');
    const [level, setLevel] = useState(question?.level || '');
    // Multi-level: a question can target several levels. `level` (primary) mirrors levels[0]
    // and continues to drive the taxonomy pickers + division/board/unit coupling.
    const [levels, setLevels] = useState(
        Array.isArray(question?.levels) && question.levels.length
            ? question.levels
            : (question?.level ? [question.level] : [])
    );
    const [selectedSubjects, setSelectedSubjects] = useState([]);
    const [selectedTopics, setSelectedTopics] = useState([]);
    const [selectedExams, setSelectedExams] = useState([]);
    const [selectedBcsTerm, setSelectedBcsTerm] = useState(question?.bcs_term || '');
    const [board, setBoard] = useState(question?.board || '');
    const [division, setDivision] = useState(question?.division || '');
    const [unit, setUnit] = useState(question?.unit || '');
    const [syllabusStatus, setSyllabusStatus] = useState(question?.syllabus_status || 'CURRENT');
    const [difficulty, setDifficulty] = useState(question?.difficulty || '');
    const [selectedYears, setSelectedYears] = useState(
        question?.year ? String(question.year).split(',').filter(Boolean) : []
    );
    const [sideText, setSideText] = useState(question?.side_text || '');
    const [audioUrl, setAudioUrl] = useState(question?.audio_url || '');
    const [solvingTimeSeconds, setSolvingTimeSeconds] = useState(
        question?.solving_time_seconds != null ? String(question.solving_time_seconds) : ''
    );
    const [questionImageUrl, setQuestionImageUrl] = useState(question?.question_image_url || '');
    const [optionImages, setOptionImages] = useState({
        A: question?.option_a_image_url || '',
        B: question?.option_b_image_url || '',
        C: question?.option_c_image_url || '',
        D: question?.option_d_image_url || '',
        E: question?.option_e_image_url || '',
        F: question?.option_f_image_url || '',
        G: question?.option_g_image_url || '',
        H: question?.option_h_image_url || '',
    });
    const [explanationImageUrl, setExplanationImageUrl] = useState(question?.explanation_image_url || '');
    // IELTS accepted-answer alternatives (TEXT) + MATCHING shared box / image, loaded from metadata.
    const [acceptedAnswers, setAcceptedAnswers] = useState(
        Array.isArray(question?.metadata?.accepted) ? question.metadata.accepted.join('\n') : ''
    );
    const [matchRows, setMatchRows] = useState(() => {
        const box = question?.metadata?.match_options;
        if (box && Object.keys(box).length) return Object.keys(box).map(l => ({ letter: l, text: box[l] }));
        return [{ letter: 'A', text: '' }, { letter: 'B', text: '' }, { letter: 'C', text: '' }];
    });
    const [matchImage, setMatchImage] = useState(question?.metadata?.match_image || '');

    const correctOption = [...correctOptions].sort().join(',');
    const isText = questionType === 'TEXT';
    const isTC = questionType === 'TEXT_COMPLETION';
    const isSE = questionType === 'SENTENCE_EQUIVALENCE';
    const isBuild = questionType === 'BUILD_SENTENCE';
    const isMatching = questionType === 'MATCHING';
    const isEssay = selectedSubjects.some(isEssaySubject);
    const optionsRequired = !isText && !isTC && !isBuild && !isMatching && !isEssay;
    const showOptionsGrid = !isText && !isTC && !isMatching;
    const visibleLetters = isSE
        ? ['A', 'B', 'C', 'D', 'E', 'F']
        : ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].slice(0, 4 + extraOptionsShown);
    const optionsByLetter = { A: optionA, B: optionB, C: optionC, D: optionD, E: optionE, F: optionF, G: optionG, H: optionH };
    const optionSetters = { A: setOptionA, B: setOptionB, C: setOptionC, D: setOptionD, E: setOptionE, F: setOptionF, G: setOptionG, H: setOptionH };
    const presentLetters = visibleLetters.filter(l => (optionsByLetter[l] || '').trim() || optionImages[l]);

    // Unsaved-change detection + guarded grid navigation (Next/Prev). Only active in the
    // Question-Management grid context (parent passes hasPrev/hasNext); other callers
    // (e.g. PassageQuestionWizard) leave them null and keep their existing behavior.
    const navEnabled = hasPrev !== null || hasNext !== null;
    const currentSig = () => editableSig({
        questionText, options: optionsByLetter, correctOptions, correctOrder, questionType,
        textAnswer, answerExplanation, levels, difficulty, selectedYears, syllabusStatus,
        selectedSubjects, selectedTopics, selectedExams, selectedBcsTerm, board, division, unit,
        sideText, audioUrl, solvingTimeSeconds, questionImageUrl, optionImages, explanationImageUrl,
        blanks, acceptedAnswers, matchRows, matchImage,
    });
    const confirmDiscardIfDirty = () =>
        !navEnabled || cleanSigRef.current === currentSig() ||
        window.confirm('You have unsaved changes. Discard them and continue?');
    // Footer Back/Next (guarded by the unsaved-changes prompt in grid context).
    const guardedBack = () => { if (confirmDiscardIfDirty()) onBack && onBack(); };
    const guardedNext = () => { if (confirmDiscardIfDirty()) onNext && onNext(); };
    // Preview Prev/Next: navigate the grid; when there's no neighbour, just close the preview.
    const previewPrev = () => { if (!hasPrev) { setShowPreview(false); return; } if (confirmDiscardIfDirty()) onBack && onBack(); };
    const previewNext = () => { if (!hasNext) { setShowPreview(false); return; } if (confirmDiscardIfDirty()) onNext && onNext(); };
    // Exam-driven fields: Board + Exam Year appear (and are required) once an exam is picked.
    const examSelected = selectedExams.length > 0;
    const divisionRequired = isDivisionLevel(level);
    const boardRequired = isBoardLevel(level) && examSelected;
    // A "real" BCS question is one whose EXAM is BCS (level alone isn't enough). Only then is a
    // BCS term the right grouping, and the term replaces the exam year (BCS sittings have no year).
    const isBcs = level === BCS_LEVEL && selectedExams.some(e => e.toUpperCase() === 'BCS');
    const bcsTermRequired = isBcs;
    const yearVisible = examSelected && !isBcs && !isLanguageLevel(level);
    const yearRequired = yearVisible;

    const [availableTags, setAvailableTags] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [showPreview, setShowPreview] = useState(false);
    // Inline "saved" confirmation shown when the modal stays open after Save (stayOpenOnSave).
    const [saveMsg, setSaveMsg] = useState('');
    // Snapshot of the question's clean (unedited) field signature, for unsaved-change detection.
    const cleanSigRef = useRef('');
    // When editing a passage member, load the shared passage so the editor shows the
    // passage context (read-only) instead of looking like a standalone question.
    const [passageGroup, setPassageGroup] = useState(null);
    const CallApi = useApiCaller();
    const voice = useVoiceDictation();

    // Initialize form fields from question prop
    useEffect(() => {
        if (question) {
            setQuestionText(question.question_text || '');
            setOptionA(question.option_a || '');
            setOptionB(question.option_b || '');
            setOptionC(question.option_c || '');
            setOptionD(question.option_d || '');
            setOptionE(question.option_e || '');
            setOptionF(question.option_f || '');
            setOptionG(question.option_g || '');
            setOptionH(question.option_h || '');
            setExtraOptionsShown((question.option_g || question.option_h) ? 4 : (question.option_e || question.option_f) ? 2 : 0);
            setBlanks(
                (question.metadata?.blanks && question.metadata.blanks.length)
                    ? question.metadata.blanks.map(b => ({ choices: [...(b.choices || ['', '', '']), '', '', ''].slice(0, 3), correct: b.correct ?? 0 }))
                    : [{ choices: ['', '', ''], correct: 0 }, { choices: ['', '', ''], correct: 0 }]
            );
            setCorrectOptions(splitCsv(question.correct_option));
            setCorrectOrder(Array.isArray(question.metadata?.correct_order) ? question.metadata.correct_order : []);
            setAcceptedAnswers(Array.isArray(question.metadata?.accepted) ? question.metadata.accepted.join('\n') : '');
            {
                const box = question.metadata?.match_options;
                setMatchRows(box && Object.keys(box).length
                    ? Object.keys(box).map(l => ({ letter: l, text: box[l] }))
                    : [{ letter: 'A', text: '' }, { letter: 'B', text: '' }, { letter: 'C', text: '' }]);
            }
            setMatchImage(question.metadata?.match_image || '');
            setQuestionType(question.question_type || 'MCQ');
            setTextAnswer(question.text_answer || '');
            setAnswerExplanation(question.answer_explanation || '');
            setLevel(question.level || '');
            setLevels(
                Array.isArray(question.levels) && question.levels.length
                    ? question.levels
                    : (question.level ? [question.level] : [])
            );
            setDifficulty(question.difficulty || '');
            setSelectedYears(question.year ? String(question.year).split(',').filter(Boolean) : []);
            setSyllabusStatus(question.syllabus_status || 'CURRENT');
            setSelectedSubjects(question.subject ? question.subject.split(',').filter(Boolean) : []);
            setSelectedTopics(question.topic ? question.topic.split(',').filter(Boolean) : []);
            setSelectedExams(question.exam ? question.exam.split(',').filter(Boolean) : []);
            setSelectedBcsTerm(question.bcs_term || '');
            setBoard(question.board || '');
            setDivision(question.division || '');
            setUnit(question.unit || '');
            setSideText(question.side_text || '');
            setAudioUrl(question.audio_url || '');
            setSolvingTimeSeconds(question.solving_time_seconds != null ? String(question.solving_time_seconds) : '');
            setQuestionImageUrl(question.question_image_url || '');
            setOptionImages({
                A: question.option_a_image_url || '',
                B: question.option_b_image_url || '',
                C: question.option_c_image_url || '',
                D: question.option_d_image_url || '',
                E: question.option_e_image_url || '',
                F: question.option_f_image_url || '',
                G: question.option_g_image_url || '',
                H: question.option_h_image_url || '',
            });
            setExplanationImageUrl(question.explanation_image_url || '');
            // Capture the clean signature so Next/Prev can detect unsaved edits.
            cleanSigRef.current = editableSig(questionToEditable(question));
            setSaveMsg('');
        }
    }, [question]);

    // Load the shared passage for a grouped question so the editor renders it as a
    // passage view. Cleared for standalone questions.
    useEffect(() => {
        let cancelled = false;
        const gid = question?.group_id;
        if (gid == null) { setPassageGroup(null); return undefined; }
        (async () => {
            try {
                const res = await CallApi({ url: getAdminQuestionGroupByIdUrl(gid), method: 'GET', token: true });
                if (!cancelled && res?.success) setPassageGroup(res.data);
            } catch (err) {
                if (!cancelled) setPassageGroup(null);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [question?.group_id]);

    const toggleCorrectOption = (opt) => {
        setCorrectOptions(prev =>
            prev.includes(opt) ? prev.filter(o => o !== opt) : [...prev, opt]
        );
    };

    // Bag for the shared AI hook + ingestion payload helpers.
    const questionFields = useMemo(() => ({
        question_text: questionText,
        option_a: optionA,
        option_b: optionB,
        option_c: optionC,
        option_d: optionD,
        correct_option: correctOption,
        level,
    }), [questionText, optionA, optionB, optionC, optionD, correctOption, level]);

    // Exclude this question from similar-search results so we don't surface
    // the row being edited as a near-duplicate of itself.
    const excludeIds = useMemo(
        () => (question?.question_id ? [question.question_id] : null),
        [question?.question_id]
    );

    // Keep the primary `level` in sync with the first selected level so the taxonomy
    // pickers and division/board/unit coupling continue to work off a single value.
    useEffect(() => {
        setLevel(levels[0] || '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [levels]);

    const ai = useQuestionAi({
        questionFields,
        excludeQuestionIds: excludeIds,
    });

    // Auto-fetch similar questions once on mount (matches the draft editor flow).
    const didInitialSimilarFetchRef = useRef(false);
    useEffect(() => {
        if (didInitialSimilarFetchRef.current) return;
        if (!level) return;
        didInitialSimilarFetchRef.current = true;
        ai.fetchSimilar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [level]);

    const fetchTagsForLevel = useCallback(async () => {
        // Fetch tags for EVERY selected level and merge, so the Exam picker can offer exams
        // across all selected levels (subject/topic stay scoped to the primary level below).
        const lvls = (levels || []).filter(l => l && l !== "All Levels");
        if (!lvls.length) { setAvailableTags([]); return; }
        try {
            const results = await Promise.all(
                lvls.map(l => CallApi({ url: getTagUrl({ level: l }), method: 'GET', token: true }))
            );
            const merged = [];
            results.forEach(r => { if (r && r.success) merged.push(...(r.data.tags || [])); });
            setAvailableTags(merged);
        } catch (err) {
            setAvailableTags([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [levels]);

    useEffect(() => {
        fetchTagsForLevel();
    }, [fetchTagsForLevel]);

    // When an exam is selected, auto-select the coupled division (SSC/HSC) or unit (VARSITY)
    // with a default if applicable and none is chosen yet.
    useEffect(() => {
        if (!examSelected) return;
        if (isUnitLevel(level) && !unit) setUnit(UNIT_VALUES[0]);
        if (isDivisionLevel(level) && !division) setDivision(DIVISION_VALUES[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [examSelected, level]);


    const handleSave = async () => {
        setError('');
        setSaveMsg('');
        // A question text / option counts as filled if it has TEXT or an IMAGE.
        const hasQuestionContent = !!(questionText || questionImageUrl);
        const optionFilled = (key, text) => !!(text || optionImages[key]);
        const allOptionsFilled = optionFilled('A', optionA) && optionFilled('B', optionB)
            && optionFilled('C', optionC) && optionFilled('D', optionD);
        const blanksValid = blanks.length >= 2 && blanks.length <= 3
            && blanks.every(b => b.choices.filter(c => (c || '').trim()).length === 3 && b.correct >= 0 && b.correct <= 2);
        const seValid = presentLetters.length === 6 && correctOptions.length === 2;
        const buildValid = presentLetters.length >= 2 && correctOrder.length >= 2
            && new Set(correctOrder).size === correctOrder.length
            && correctOrder.every(i => Number.isInteger(i) && i >= 0 && i < presentLetters.length);
        const filledMatchRows = matchRows.filter(r => (r.text || '').trim());
        const matchValid = filledMatchRows.length >= 2 && correctOptions.length >= 1
            && correctOptions.every(l => filledMatchRows.some(r => r.letter === l));
        const answerValid = isText ? true : isTC ? blanksValid : isSE ? seValid : isBuild ? buildValid : isMatching ? matchValid
            : (isEssay || (allOptionsFilled && correctOptions.length > 0 && correctOptions.every(o => presentLetters.includes(o))));
        const baseValid = hasQuestionContent && level && difficulty;
        if (!baseValid || !answerValid) {
            if (isTC && !blanksValid) setError('Text Completion needs 2–3 blanks, each with 3 choices and a correct pick.');
            else if (isSE && !seValid) setError('Sentence Equivalence needs exactly 6 options and exactly 2 correct.');
            else if (isBuild && !buildValid) setError('Build a Sentence needs ≥2 word tiles and at least 2 placed in the correct order (leftover tiles are distractors).');
            else if (isMatching && !matchValid) setError('Matching needs at least 2 option-box entries and a correct letter chosen for this item.');
            else setError('Please fill all required fields (text or image is fine).');
            return;
        }
        if (isUnitLevel(level) && !unit) {
            setError('Unit is required for VARSITY questions.');
            return;
        }
        if (divisionRequired && !division) {
            setError('Division is required for SSC/HSC questions.');
            return;
        }
        if (bcsTermRequired && !selectedBcsTerm) {
            setError('BCS Term is required for BCS questions.');
            return;
        }
        if (yearRequired && selectedYears.length === 0) {
            setError('Exam Year is required when an exam is selected.');
            return;
        }
        if (boardRequired && !board) {
            setError('Board is required when an exam is selected.');
            return;
        }
        setIsSaving(true);
        try {
            const options = {};
            const optionImagesByField = {};
            if (showOptionsGrid) {
                presentLetters.forEach(l => {
                    options[`option_${l.toLowerCase()}`] = optionsByLetter[l] || '';
                    if (optionImages[l]) optionImagesByField[`option_${l.toLowerCase()}`] = optionImages[l];
                });
            }
            const acceptedList = acceptedAnswers.split(/[\n,]/).map(s => s.trim()).filter(Boolean);
            const matchBox = {};
            matchRows.forEach(r => { if ((r.text || '').trim()) matchBox[r.letter] = r.text.trim(); });
            let metadata;
            if (isTC) {
                metadata = { blanks: blanks.map(b => ({ choices: b.choices.map(c => (c || '').trim()), correct: b.correct })) };
            } else if (isBuild) {
                metadata = { ...(question?.metadata || {}), correct_order: correctOrder };
            } else if (isMatching) {
                metadata = { ...(question?.metadata || {}), match_options: matchBox };
                if (matchImage.trim()) metadata.match_image = matchImage.trim(); else delete metadata.match_image;
            } else if (isText) {
                metadata = { ...(question?.metadata || {}) };
                if (acceptedList.length) metadata.accepted = acceptedList; else delete metadata.accepted;
                if (Object.keys(metadata).length === 0) metadata = null;
            } else {
                metadata = (question?.metadata ?? null);
            }
            const payload = constructQuestionPostPayload({
                text: questionText,
                options,
                optionImages: optionImagesByField,
                correctOption: (isTC || isBuild) ? '' : correctOption,
                answerExplanation,
                level,
                levels,
                topic: selectedTopics.join(','),
                subject: selectedSubjects.join(','),
                exam: selectedExams.join(','),
                difficulty,
                year: isBcs ? '' : selectedYears.join(','),
                bcsTerm: isBcs ? selectedBcsTerm : '',
                syllabusStatus,
                questionType,
                textAnswer,
                board, division, unit,
                sideText, audioUrl, solvingTimeSeconds,
                questionImageUrl,
                explanationImageUrl,
                metadata,
            });
            const newQuestionPutUrl = getAdminQuestionPutUrl(question.question_id);
            const response = await CallApi({
                url: newQuestionPutUrl,
                method: 'PUT',
                payload: payload,
                token: true
            });
            if (response.success) {
                // Row patch (grid-row shape) so the parent can update the grid + nav in place
                // without a full reload — and so reopening the row shows the saved changes.
                if (onSaved) {
                    onSaved({
                        question_id: question.question_id,
                        question_text: questionText,
                        option_a: optionA, option_b: optionB, option_c: optionC, option_d: optionD,
                        option_e: optionE, option_f: optionF, option_g: optionG, option_h: optionH,
                        option_a_image_url: optionImages.A || '', option_b_image_url: optionImages.B || '',
                        option_c_image_url: optionImages.C || '', option_d_image_url: optionImages.D || '',
                        option_e_image_url: optionImages.E || '', option_f_image_url: optionImages.F || '',
                        option_g_image_url: optionImages.G || '', option_h_image_url: optionImages.H || '',
                        correct_option: (isTC || isBuild) ? '' : correctOption,
                        answer_explanation: answerExplanation,
                        question_type: questionType,
                        text_answer: textAnswer,
                        level, levels,
                        subject: selectedSubjects.join(','),
                        topic: selectedTopics.join(','),
                        exam: selectedExams.join(','),
                        difficulty,
                        year: isBcs ? '' : selectedYears.join(','),
                        bcs_term: isBcs ? selectedBcsTerm : '',
                        board, division, unit,
                        side_text: sideText, audio_url: audioUrl,
                        solving_time_seconds: solvingTimeSeconds ? Number(solvingTimeSeconds) : null,
                        question_image_url: questionImageUrl,
                        explanation_image_url: explanationImageUrl,
                        syllabus_status: syllabusStatus,
                        metadata,
                    });
                }
                if (stayOpenOnSave) {
                    // Stay on the edit page: show a green confirmation, reset the unsaved-changes
                    // baseline to the saved values, and leave the modal open (the grid was patched
                    // in place above — no reload needed).
                    setSaveMsg('✓ Changes saved.');
                    cleanSigRef.current = currentSig();
                } else {
                    onSave();
                }
            } else {
                setError(response.message || 'Failed to update question.');
            }
        } catch (e) {
            setError('An error occurred.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleOptionChange = (optionKey, value) => {
        const setter = optionSetters[optionKey];
        if (setter) setter(value);
    };

    const optionsMap = optionsByLetter;

    const handleTagChange = (setter, currentTags, newValue) => {
        if (newValue && !currentTags.includes(newValue)) {
            setter([...currentTags, newValue]);
        }
    };

    const handleRemoveTag = (setter, currentTags, tagToRemove) => {
        setter(currentTags.filter(tag => tag !== tagToRemove));
    };

    const getUniqueTagsForType = (type) => {
        return [...new Set(availableTags.filter(tag => tag.tag_type === type && tag.level === level).map(tag => tag.name))];
    };

    // Exam options span EVERY selected level (a multi-level question may draw exams from any of
    // its levels), deduped by name. Subject/topic stay scoped to the primary level above.
    const getExamOptions = () => {
        const lvls = new Set((levels || []).filter(Boolean));
        return [...new Set(
            availableTags.filter(tag => tag.tag_type === "EXAM" && lvls.has(tag.level)).map(tag => tag.name)
        )];
    };

    // Topics narrowed to the selected level + subject(s) via the tag's subject field (fallback:
    // legacy metadata.subject). Topics with no subject are kept (degrades to level-only).
    const getTopicOptions = () => {
        const subs = selectedSubjects || [];
        return [...new Set(
            availableTags
                .filter(t => t.tag_type === 'TOPIC' && t.level === level)
                .filter(t => {
                    const s = t.subject || (t.metadata && t.metadata.subject) || '';
                    return !s || subs.length === 0 || subs.includes(s);
                })
                .map(t => t.name)
        )];
    };

    const bcsTermTags = level === BCS_LEVEL
        ? availableTags.filter(t => t.tag_type === 'BCS_TERM' && t.level === BCS_LEVEL)
        : [];
    const bcsTermNames = sortBcsTerms(bcsTermTags.map(t => t.name));

    const handleBcsTermChange = (newTerm) => {
        setSelectedBcsTerm(newTerm);
    };

    const topSimilarity = ai.similar.length > 0 ? ai.similar[0].similarity_score : 0;
    const showDuplicateWarning = ai.similarFetched && topSimilarity >= SIMILARITY_THRESHOLDS.DUPLICATE;

    return (
        <div
            className="modal d-block"
            tabIndex="-1"
            role="dialog"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        >
            <div
                className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
                role="document"
                style={{ maxWidth: '1500px', width: '95vw' }}
            >
                <div className="modal-content kp-qform">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            {title || `Edit Question (ID: ${question?.question_id})`}
                            {question?.group_id != null && (
                                <span className="badge bg-light text-dark ms-2" title={`Passage group #${question.group_id}`}>📘 Passage #{question.group_id}{question.group_order != null ? ` · Q${question.group_order}` : ''}</span>
                            )}
                        </h5>
                        <div className="d-flex align-items-center gap-2">
                            <button type="button" className="btn btn-sm" style={{ background: "linear-gradient(145deg, #8b5cf6, #6d28d9)", color: "#fff", border: "none", fontWeight: 600, whiteSpace: "nowrap", marginLeft: "1rem" }} onClick={() => setShowPreview(true)}>👁 Preview Question In Test</button>
                            <CloseButton onClick={onCancel} />
                        </div>
                    </div>
                    <div className="modal-body" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'hidden' }}>
                        <AuditHeader question={question} />

                        {question?.group_id != null && (
                            <div className="mb-3" style={{ background: '#faf5ff', border: '1px solid #e9d5ff', borderLeft: '4px solid #7c3aed', borderRadius: '0.5rem', padding: '0.75rem 1rem' }}>
                                <div className="d-flex align-items-center justify-content-between mb-1">
                                    <strong style={{ color: '#5b21b6' }}>📘 Passage #{question.group_id}{question.group_order != null ? ` · question ${question.group_order}` : ''}</strong>
                                    {passageGroup && <span className="small text-muted">{passageGroup.question_count} question{passageGroup.question_count === 1 ? '' : 's'} in this passage</span>}
                                </div>
                                {passageGroup
                                    ? (
                                        <>
                                            <div style={{ whiteSpace: 'pre-wrap', color: '#374151', maxHeight: '9rem', overflowY: 'auto' }}>
                                                <MathText text={passageGroup.passage_text || '(no passage text)'} />
                                            </div>
                                            {passageGroup.audio_url && (
                                                <audio controls src={passageGroup.audio_url} style={{ width: '100%', marginTop: '0.5rem' }} />
                                            )}
                                            <div className="small text-muted mt-1">This passage is shared by all its questions — edit it from Passage Management.</div>
                                        </>
                                    )
                                    : <div className="small text-muted">Loading passage…</div>}
                            </div>
                        )}

                        {showDuplicateWarning && (
                            <div className="alert alert-warning py-2 mb-3" role="alert">
                                <strong>⚠ Near-duplicate detected:</strong>{' '}
                                this question is {topSimilarity.toFixed(2)} similar to question #{ai.similar[0].question_id}.
                                Review the right panel before saving.
                            </div>
                        )}
                        {error && <div className="alert alert-danger py-2 mb-3">{error}</div>}

                        <div className="row g-3" style={{ height: 'calc(100vh - 320px)' }}>
                            <div className="col-md-7" style={{ overflowY: 'auto', maxHeight: '100%', overscrollBehavior: 'contain' }}>
                                <div className="d-flex align-items-center mb-3 gap-2">
                                    <small className="text-muted">Voice input:</small>
                                    <div className="btn-group btn-group-sm" role="group" aria-label="Voice language">
                                        <button
                                            type="button"
                                            className={`btn ${voice.language === 'en' ? 'btn-info' : 'btn-outline-info'}`}
                                            onClick={() => voice.setLanguage('en')}
                                            disabled={!voice.supported}
                                        >English</button>
                                        <button
                                            type="button"
                                            className={`btn ${voice.language === 'bn' ? 'btn-info' : 'btn-outline-info'}`}
                                            onClick={() => voice.setLanguage('bn')}
                                            disabled={!voice.supported}
                                        >বাংলা</button>
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Level(s) <span className="kp-req">*</span></label>
                                    <div className="d-flex flex-wrap gap-2 mb-2">
                                        {levels.map((lv, i) => (
                                            <span key={lv} className={`badge d-flex align-items-center rounded-pill px-2 py-1 ${i === 0 ? 'bg-primary-subtle text-primary' : 'bg-secondary-subtle text-secondary'}`}>
                                                {lv}{i === 0 ? ' (primary)' : ''}
                                                <button type="button" className="btn-close ms-1 text-danger" aria-label="Remove" onClick={() => handleRemoveTag(setLevels, levels, lv)}></button>
                                            </span>
                                        ))}
                                    </div>
                                    <select className="form-select" value="" onChange={e => handleTagChange(setLevels, levels, e.target.value)}>
                                        <option value="">Add Level</option>
                                        {TAGLEVELS.map(item => item.submenu ? <optgroup key={item.value} label={item.label}>{item.submenu.map(sub => <option key={sub.value} value={sub.value}>{sub.label}</option>)}</optgroup> : <option key={item.value} value={item.value}>{item.label}</option>)}
                                    </select>
                                    <small className="form-text text-muted">Pick one or more levels. The first is the primary and drives the subject/topic pickers.</small>
                                </div>
                                {isDivisionLevel(level) && (
                                    <div className="mb-3">
                                        <label className="form-label">Division <span className="kp-req">*</span></label>
                                        <select className="form-select" value={division} onChange={e => setDivision(e.target.value)} required>
                                            <option value="">Select division</option>
                                            {DIVISION_VALUES.map(d => <option key={d} value={d}>{d}</option>)}
                                        </select>
                                        <small className="form-text text-muted">Required for SSC/HSC questions.</small>
                                    </div>
                                )}
                                {isUnitLevel(level) && (
                                    <div className="mb-3">
                                        <label className="form-label">Unit <span className="kp-req">*</span></label>
                                        <select className="form-select" value={unit} onChange={e => setUnit(e.target.value)} required>
                                            <option value="">Select unit</option>
                                            {UNIT_VALUES.map(u => <option key={u} value={u}>{u}</option>)}
                                        </select>
                                        <small className="form-text text-muted">Required for VARSITY questions.</small>
                                    </div>
                                )}
                                {/* Subject Multi-select — placed right after Level for a natural top-down flow. */}
                                <div className="mb-3">
                                    <label className="form-label">Subject <span className="kp-req">*</span></label>
                                    <div className="d-flex flex-wrap gap-2 mb-2">
                                        {selectedSubjects.map(tag => (
                                            <span key={tag} className="badge bg-info-subtle text-info d-flex align-items-center rounded-pill px-2 py-1">
                                                {tag}
                                                <button type="button" className="btn-close ms-1 text-danger" aria-label="Remove" onClick={() => handleRemoveTag(setSelectedSubjects, selectedSubjects, tag)}></button>
                                            </span>
                                        ))}
                                    </div>
                                    <select className="form-select" value="" onChange={e => handleTagChange(setSelectedSubjects, selectedSubjects, e.target.value)} disabled={!level || getUniqueTagsForType("SUBJECT").length === 0}>
                                        <option value="">Add Subject</option>
                                        {getUniqueTagsForType("SUBJECT").map(tag => <option key={tag} value={tag}>{tag}</option>)}
                                    </select>
                                    {!level && <small className="form-text text-muted">Select a Level to add Subjects.</small>}
                                </div>

                                {/* Topic Multi-select */}
                                <div className="mb-3">
                                    <label className="form-label">Topic</label>
                                    <div className="d-flex flex-wrap gap-2 mb-2">
                                        {selectedTopics.map(tag => (
                                            <span key={tag} className="badge bg-warning-subtle text-warning d-flex align-items-center rounded-pill px-2 py-1">
                                                {tag}
                                                <button type="button" className="btn-close ms-1 text-danger" aria-label="Remove" onClick={() => handleRemoveTag(setSelectedTopics, selectedTopics, tag)}></button>
                                            </span>
                                        ))}
                                    </div>
                                    <select className="form-select" value="" onChange={e => handleTagChange(setSelectedTopics, selectedTopics, e.target.value)} disabled={!level || getTopicOptions().length === 0}>
                                        <option value="">Add Topic</option>
                                        {getTopicOptions().map(tag => <option key={tag} value={tag}>{tag}</option>)}
                                    </select>
                                    {!level && <small className="form-text text-muted">Select a Level to add Topics.</small>}
                                </div>

                                <div className="mb-3">
                                    <label className="form-label me-3">Question Type</label>
                                    {QUESTION_TYPES.map(qt => (
                                        <div className="form-check form-check-inline" key={qt}>
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="question_type_edit"
                                                id={`qtype-edit-${qt}`}
                                                checked={questionType === qt}
                                                onChange={() => setQuestionType(qt)}
                                            />
                                            <label className="form-check-label" htmlFor={`qtype-edit-${qt}`}>
                                                {QUESTION_TYPE_LABELS[qt] || qt}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                                {isLanguageLevel(level) && (
                                    <>
                                        <div className="mb-3">
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <label className="form-label mb-0">Side text / passage (optional)</label>
                                                <VoiceInputButton
                                                    fieldKey="side_text"
                                                    listeningField={voice.listeningField}
                                                    supported={voice.supported}
                                                    onClick={() => voice.toggle('side_text', sideText, setSideText)}
                                                />
                                            </div>
                                            <textarea className="form-control" value={sideText} onChange={e => setSideText(e.target.value)} onBlur={() => setSideText(normalizeFieldText(sideText))}></textarea>
                                        </div>
                                        <MediaUploadField label="Audio (optional)" value={audioUrl} onChange={setAudioUrl} kind="audio" />
                                    </>
                                )}
                                <div className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <label className="form-label mb-0">Question Text <span className="kp-req">*</span></label>
                                        <VoiceInputButton
                                            fieldKey="question"
                                            listeningField={voice.listeningField}
                                            supported={voice.supported}
                                            onClick={() => voice.toggle('question', questionText, setQuestionText)}
                                        />
                                    </div>
                                    <MediaUploadField label="Question image (optional)" value={questionImageUrl} onChange={setQuestionImageUrl} kind="image" />
                                    <textarea className="form-control" value={questionText} onChange={e => setQuestionText(e.target.value)} onBlur={() => setQuestionText(normalizeFieldText(questionText, { dashWhenUnsure: true }))} required></textarea>
                                    {questionText && questionText.indexOf('$') !== -1 && (
                                        <div className="small text-muted mt-1">Preview: <MathText text={questionText} /></div>
                                    )}
                                </div>
                                {isText && (
                                    <div className="mb-3">
                                        <label className="form-label mb-0">Text Answer (reference)</label>
                                        <small className="form-text text-muted d-block">Set a reference to auto-grade (case/space-insensitive). Leave blank to grade manually. Answer-key notation is accepted: <code>10/ten</code>, <code>grass(es)</code>, <code>(land) surface</code>.</small>
                                        <textarea className="form-control" value={textAnswer} onChange={e => setTextAnswer(e.target.value)}></textarea>
                                        <label className="form-label mb-0 mt-2">Accepted alternatives (optional)</label>
                                        <small className="form-text text-muted d-block">One per line or comma-separated — any of these also counts as correct.</small>
                                        <textarea className="form-control" rows={2} value={acceptedAnswers} onChange={e => setAcceptedAnswers(e.target.value)} placeholder="e.g. colour&#10;color"></textarea>
                                    </div>
                                )}
                                {isMatching && (
                                    <div className="mb-3 border rounded p-2">
                                        <label className="form-label mb-1 fw-bold">Matching option box (shared)</label>
                                        <small className="form-text text-muted d-block mb-2">The list of options A, B, C… that every item in this set chooses from. Tick the one(s) that are correct for THIS item.</small>
                                        {matchRows.map((row, i) => (
                                            <div key={i} className="d-flex align-items-center gap-2 mb-1">
                                                <span className="fw-bold" style={{ width: '1.5rem' }}>{row.letter}</span>
                                                <input className="form-control form-control-sm" value={row.text}
                                                    onChange={e => setMatchRows(rows => rows.map((r, j) => j === i ? { ...r, text: e.target.value } : r))}
                                                    placeholder={`Option ${row.letter} text`} />
                                                <div className="form-check mb-0" title="Correct for this item">
                                                    <input type="checkbox" className="form-check-input" checked={correctOptions.includes(row.letter)}
                                                        onChange={() => setCorrectOptions(cur => cur.includes(row.letter) ? cur.filter(l => l !== row.letter) : [...cur, row.letter])} />
                                                    <label className="form-check-label small">correct</label>
                                                </div>
                                                {matchRows.length > 2 && (
                                                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setMatchRows(rows => rows.filter((_, j) => j !== i))}>×</button>
                                                )}
                                            </div>
                                        ))}
                                        {matchRows.length < 10 && (
                                            <button type="button" className="btn btn-sm btn-outline-secondary mt-1"
                                                onClick={() => setMatchRows(rows => [...rows, { letter: String.fromCharCode(65 + rows.length), text: '' }])}>
                                                + Add option
                                            </button>
                                        )}
                                        <div className="mt-2">
                                            <MediaUploadField label="Shared map / plan / diagram image (optional)" value={matchImage} onChange={setMatchImage} kind="image" />
                                        </div>
                                    </div>
                                )}
                                {showOptionsGrid && visibleLetters.map((opt) => (
                                    <div className="mb-3" key={opt}>
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <label className="form-label mb-0">Option {opt}{optionsRequired && opt <= 'D' ? <span className="kp-req"> *</span> : ''}</label>
                                            {opt <= 'D' && (
                                                <VoiceInputButton
                                                    fieldKey={`option${opt}`}
                                                    listeningField={voice.listeningField}
                                                    supported={voice.supported}
                                                    onClick={() => voice.toggle(`option${opt}`, optionsMap[opt], (text) => handleOptionChange(opt, text))}
                                                />
                                            )}
                                        </div>
                                        <MediaUploadField label={`Option ${opt} image (optional)`} value={optionImages[opt]} onChange={(url) => setOptionImages(prev => ({ ...prev, [opt]: url }))} kind="image" />
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={optionsMap[opt]}
                                            onChange={e => handleOptionChange(opt, e.target.value)}
                                            onBlur={() => handleOptionChange(opt, normalizeFieldText(optionsMap[opt]))}
                                            required={optionsRequired && opt <= 'D'}
                                        />
                                    </div>
                                ))}

                                {showOptionsGrid && !isSE && extraOptionsShown < 4 && (
                                    <button type="button" className="btn btn-sm btn-outline-secondary mb-3"
                                        onClick={() => setExtraOptionsShown(n => Math.min(4, n + 2))}>
                                        + Add two more options
                                    </button>
                                )}
                                {isSE && (
                                    <div className="form-text text-muted mb-2">Sentence Equivalence: fill all 6 options and tick exactly the 2 correct answers.</div>
                                )}

                                {showOptionsGrid && !isBuild && (
                                    <div className="mb-3">
                                        <label className="form-label mb-1 d-block">Correct Option(s){optionsRequired ? <span className="kp-req"> *</span> : ''}</label>
                                        <div className="d-flex flex-wrap gap-3">
                                            {presentLetters.map(opt => (
                                                <div className="form-check" key={opt}>
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id={`correct-edit-${opt}`}
                                                        checked={correctOptions.includes(opt)}
                                                        onChange={() => toggleCorrectOption(opt)}
                                                    />
                                                    <label className="form-check-label" htmlFor={`correct-edit-${opt}`}>{opt}</label>
                                                </div>
                                            ))}
                                        </div>
                                        <small className="form-text text-muted">
                                            {isSE ? 'Tick exactly two correct options.' : 'Tick every correct option — supports “select all that apply”. Only filled options are shown.'}
                                        </small>
                                    </div>
                                )}

                                {isBuild && (
                                    <div className="mb-3">
                                        <label className="form-label mb-1 d-block">Correct word order <span className="kp-req">*</span></label>
                                        <div className="form-text text-muted mb-2">Fill the option fields above with the (shuffled) word tiles, then click them in the correct sentence order. Leftover tiles act as distractors — you don't have to place every tile.</div>
                                        <div className="border rounded p-2 mb-2 d-flex flex-wrap gap-2" style={{ minHeight: '2.2rem' }}>
                                            {correctOrder.length === 0 && <span className="text-muted">Click tiles below to set the order…</span>}
                                            {correctOrder.filter(i => i < presentLetters.length).map((i, pos) => (
                                                <button type="button" key={pos} className="btn btn-sm btn-primary" onClick={() => setCorrectOrder(o => o.filter(x => x !== i))} title="Click to remove">
                                                    {pos + 1}. {optionsByLetter[presentLetters[i]]}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="d-flex flex-wrap gap-2">
                                            {presentLetters.map((opt, i) => (
                                                <button type="button" key={opt} disabled={correctOrder.includes(i)} className="btn btn-sm btn-outline-secondary"
                                                    onClick={() => setCorrectOrder(o => [...o, i])}>
                                                    {optionsByLetter[opt] || opt}
                                                </button>
                                            ))}
                                        </div>
                                        {correctOrder.length > 0 && (
                                            <button type="button" className="btn btn-sm btn-link text-danger p-0 mt-1" onClick={() => setCorrectOrder([])}>Reset order</button>
                                        )}
                                    </div>
                                )}

                                {isTC && (
                                    <div className="mb-3">
                                        <label className="form-label mb-1 d-block">Blanks <span className="kp-req">*</span></label>
                                        <div className="form-text text-muted mb-2">Use markers like (i), (ii) in the question text. Each blank has 3 choices; select the correct one. All-or-nothing scoring.</div>
                                        {blanks.map((blank, bi) => (
                                            <div className="border rounded p-2 mb-2" key={bi}>
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <span className="fw-semibold">Blank {TC_BLANK_LABELS[bi] || `#${bi + 1}`}</span>
                                                    {blanks.length > 1 && (
                                                        <button type="button" className="btn btn-sm btn-outline-danger"
                                                            title={`Remove Blank ${TC_BLANK_LABELS[bi] || `#${bi + 1}`}`}
                                                            onClick={() => setBlanks(prev => prev.filter((_, i) => i !== bi))}>×</button>
                                                    )}
                                                </div>
                                                {blank.choices.map((choice, ci) => (
                                                    <div className="d-flex align-items-center gap-2 mb-1" key={ci}>
                                                        <input
                                                            type="radio"
                                                            name={`tc-edit-blank-${bi}`}
                                                            checked={blank.correct === ci}
                                                            onChange={() => setBlanks(prev => prev.map((b, i) => i === bi ? { ...b, correct: ci } : b))}
                                                            title="Mark correct"
                                                        />
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-sm"
                                                            placeholder={`Choice ${ci + 1}`}
                                                            value={choice}
                                                            onChange={e => setBlanks(prev => prev.map((b, i) => i === bi ? { ...b, choices: b.choices.map((c, j) => j === ci ? e.target.value : c) } : b))}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        ))}
                                        <div className="d-flex gap-2">
                                            {blanks.length < 3 && (
                                                <button type="button" className="btn btn-sm btn-outline-secondary"
                                                    onClick={() => setBlanks(prev => [...prev, { choices: ['', '', ''], correct: 0 }])}>+ Add blank</button>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-1 flex-wrap gap-2">
                                        <label className="form-label mb-0">Explanation</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                title="Collapse line breaks into one clean paragraph"
                                                onClick={() => setAnswerExplanation(beautifyText(answerExplanation))}
                                            >Beautify</button>
                                            <VoiceInputButton
                                                fieldKey="explanation"
                                                listeningField={voice.listeningField}
                                                supported={voice.supported}
                                                onClick={() => voice.toggle('explanation', answerExplanation, setAnswerExplanation)}
                                            />
                                        </div>
                                    </div>
                                    <MediaUploadField label="Explanation image (optional)" value={explanationImageUrl} onChange={setExplanationImageUrl} kind="image" />
                                    <textarea className="form-control" value={answerExplanation} onChange={e => setAnswerExplanation(e.target.value)} onBlur={() => setAnswerExplanation(normalizeFieldText(answerExplanation))}></textarea>
                                    {answerExplanation && answerExplanation.indexOf('$') !== -1 && (
                                        <div className="small text-muted mt-1">Preview: <MathText text={answerExplanation} /></div>
                                    )}
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Difficulty <span className="kp-req">*</span></label>
                                    <select className="form-select" value={difficulty} onChange={e => setDifficulty(e.target.value)} required>
                                        <option value="">Select</option>
                                        {QUESTIONDIFFICULTY.map(diff => <option key={diff} value={diff}>{diff}</option>)}
                                    </select>
                                </div>

                                {/* Exam Multi-select — hidden for IELTS/TOEFL/GRE */}
                                {!isLanguageLevel(level) && (
                                <div className="mb-3">
                                    <label className="form-label">Exam</label>
                                    <div className="d-flex flex-wrap gap-2 mb-2">
                                        {selectedExams.map(tag => (
                                            <span key={tag} className="badge bg-danger-subtle text-danger d-flex align-items-center rounded-pill px-2 py-1">
                                                {tag}
                                                <button type="button" className="btn-close ms-1 text-danger" aria-label="Remove" onClick={() => handleRemoveTag(setSelectedExams, selectedExams, tag)}></button>
                                            </span>
                                        ))}
                                    </div>
                                    <select className="form-select" value="" onChange={e => handleTagChange(setSelectedExams, selectedExams, e.target.value)} disabled={levels.length === 0 || getExamOptions().length === 0}>
                                        <option value="">Add Exam</option>
                                        {getExamOptions().map(tag => <option key={tag} value={tag}>{tag}</option>)}
                                    </select>
                                    {levels.length === 0 && <small className="form-text text-muted">Select a Level to add Exams.</small>}
                                </div>
                                )}

                                {isBoardLevel(level) && examSelected && (
                                    <div className="mb-3">
                                        <label className="form-label">Board <span className="kp-req">*</span></label>
                                        <select className="form-select" value={board} onChange={e => setBoard(e.target.value)} required>
                                            <option value="">Select board</option>
                                            {BOARD_VALUES.map(b => <option key={b} value={b}>{b}</option>)}
                                        </select>
                                        <small className="form-text text-muted">Required when an exam is selected.</small>
                                    </div>
                                )}
                                {isTimerLevel(level) && (
                                    <div className="mb-3">
                                        <label className="form-label">Solving time (seconds, optional)</label>
                                        <input type="number" min="0" className="form-control" value={solvingTimeSeconds} onChange={e => setSolvingTimeSeconds(e.target.value)} />
                                    </div>
                                )}

                                {isBcs && (
                                    <div className="mb-3">
                                        <label className="form-label">BCS Term <span className="kp-req">*</span></label>
                                        <select
                                            className="form-select"
                                            value={selectedBcsTerm}
                                            onChange={e => handleBcsTermChange(e.target.value)}
                                            disabled={bcsTermNames.length === 0}
                                            required
                                        >
                                            <option value="">Select BCS term</option>
                                            {bcsTermNames.map(name => (
                                                <option key={name} value={name}>{name}</option>
                                            ))}
                                        </select>
                                        <small className="form-text text-muted">
                                            Required for BCS exam questions. The question is auto-added to this term's Previous-Year test. Add new terms in the Tag page.
                                        </small>
                                    </div>
                                )}

                                {yearVisible && (
                                <div className="mb-3">
                                    <label className="form-label">Exam Year {yearRequired ? <span className="kp-req">*</span> : <span className="text-muted">(optional)</span>}</label>
                                    <div className="d-flex flex-wrap gap-2 mb-2">
                                        {selectedYears.map(y => (
                                            <span key={y} className="badge bg-secondary-subtle text-secondary d-flex align-items-center rounded-pill px-2 py-1">
                                                {y}
                                                <button type="button" className="btn-close ms-1 text-danger" aria-label="Remove" onClick={() => handleRemoveTag(setSelectedYears, selectedYears, y)}></button>
                                            </span>
                                        ))}
                                    </div>
                                    {(() => {
                                        const currentYear = new Date().getFullYear();
                                        return (
                                            <YearPicker
                                                value=""
                                                onChange={y => handleTagChange(setSelectedYears, selectedYears, y)}
                                                minYear={currentYear - 20}
                                                maxYear={currentYear}
                                                placeholder="Add Exam Year"
                                                isClearable={false}
                                            />
                                        );
                                    })()}
                                    <small className="form-text text-muted">{yearRequired ? 'Required when an exam is selected — the exam year(s) the question appeared in.' : 'Optional — the exam year(s) the question appeared in.'}</small>
                                </div>
                                )}

                                <div className="mb-3">
                                    <label className="form-label">Syllabus status</label>
                                    <select className="form-select" value={syllabusStatus} onChange={e => setSyllabusStatus(e.target.value)}>
                                        {SYLLABUS_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                    <small className="form-text text-muted">Current = served everywhere. Outdated = Previous-Year bank only. Withdrawn = hidden everywhere.</small>
                                </div>

                                {/* Similar Existing Question — single top match, at the bottom of the details column. */}
                                <div className="mt-3 pt-3 border-top">
                                    <h6 className="mb-2">Similar Existing Question</h6>
                                    <SimilarQuestionsPanel
                                        loading={ai.similarLoading}
                                        items={ai.similar}
                                        hasFetched={ai.similarFetched}
                                        currentQuestionId={question?.question_id ?? null}
                                    />
                                </div>
                            </div>

                            <div
                                className="col-md-5 border-start ps-3 d-flex flex-column"
                                style={{ maxHeight: '100%', overflow: 'hidden' }}
                            >
                                {setSourceDoc && (
                                    <SourceDocViewer
                                        doc={sourceDoc}
                                        onDoc={setSourceDoc}
                                        onClose={() => setSourceDoc(null)}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        {saveMsg && <span className="text-success fw-semibold me-2">{saveMsg}</span>}
                        <button
                            type="button"
                            className="btn btn-outline-info me-auto"
                            onClick={ai.fetchSimilar}
                            disabled={ai.similarLoading || isSaving}
                        >
                            {ai.similarLoading ? 'Loading…' : 'Re-fetch Relevant Questions'}
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={isSaving}>Cancel</button>
                        {onBack && hasPrev !== false && (
                            <button type="button" className="btn btn-outline-secondary" onClick={guardedBack} disabled={isSaving}>{backLabel}</button>
                        )}
                        {onNext && hasNext !== false && (
                            <button type="button" className="btn btn-outline-secondary" onClick={guardedNext} disabled={isSaving}>{nextLabel}</button>
                        )}
                        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
                            {isSaving ? 'Saving…' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            </div>
            <QuestionPreviewModal
                open={showPreview}
                onClose={() => setShowPreview(false)}
                fields={{
                    questionText, questionType, correctOptions,
                    options: optionsByLetter, optionImages,
                    questionImageUrl, blanks, textAnswer, sideText, audioUrl,
                    correctTextAnswer: textAnswer, answerExplanation, explanationImageUrl,
                }}
                serialLabel={serialLabel}
                onPrev={navEnabled ? previewPrev : null}
                onNext={navEnabled ? previewNext : null}
                sourceDoc={sourceDoc}
                setSourceDoc={setSourceDoc}
            />
        </div>
    );
};

export default QuestionEditForm;
